import {contentKey, createMemo} from '../../../memo';
import {deepEqual, diffRows, MyCheckFieldChange, RowDiff} from './mycheck-diff';

/** Zugriff des Editors auf die Zeilen einer Parameterliste. */
export interface RowDecisionHost<T> {
  /** Die Zeilen, wie sie im Formular stehen (nach dem Anwenden des Vorschlags). */
  rows: () => T[];
  setRows: (rows: T[]) => void;
  /** Zeilen, die mit dem Vorher-Stand verglichen werden, falls das Formular zusätzliche (abgeleitete) Zeilen hält. */
  comparableRows?: () => T[];
  /** Wird nach jeder Einzelentscheidung aufgerufen. */
  decided: () => void;
}

/** Leere Werte (nicht gesetzt, '' und bei Optionen []) gelten als gleich, wie auch in {@link diffRows}. */
function sameFieldValue(field: string, a: unknown, b: unknown): boolean {
  const normalize = (value: unknown): string => JSON.stringify(value ?? (field === 'options' ? [] : ''));
  return normalize(a) === normalize(b);
}

/**
 * Zeilenweise Entscheidung über einen Vorschlag für eine Parameterliste (Eingabeparameter, Tags,
 * Ausgabeparameter …): jede neue oder geänderte Zeile bekommt ihr eigenes ✓/✗, entfernte Zeilen
 * erscheinen als eigene Zeile. Der Editor hält je Liste eine Instanz und lässt die Sammelmarke des
 * Set-Felds stehen, bis {@link pending} false ist.
 *
 * Zeilen sind Objekt-Referenzen; die Listen werden im Editor in place bearbeitet, die Referenzen
 * bleiben also über Eingaben hinweg gültig. Baut der Editor eine Liste neu auf, hängt {@link rebind}
 * die offenen Zeilen an die neuen Objekte um.
 */
export class RowDecisions<T extends object> {
  diff: RowDiff<T> | null = null;
  /** true, sobald eine Zeile einzeln übernommen wurde (Verlassen speichert dann, statt zu verwerfen). */
  accepted = false;
  private readonly memo = createMemo();
  private readonly noRows: { before: T; index: number }[] = [];

  constructor(private readonly host: RowDecisionHost<T>) {
  }

  /** Noch mindestens eine Zeile offen? */
  get pending(): boolean {
    const diff = this.diff;
    return !!diff && (diff.added.length > 0 || diff.changed.size > 0 || diff.removed.length > 0);
  }

  /** Zeilen, die der Vorschlag entfernt (stehen nicht mehr in der Liste). */
  get removed(): { before: T; index: number }[] {
    return this.diff?.removed ?? this.noRows;
  }

  reset(): void {
    this.diff = null;
    this.accepted = false;
  }

  /** Berechnet, welche Zeilen der Vorschlag neu anlegt, ändert oder entfernt. */
  init(before: T[]): void {
    this.reset();
    this.diff = diffRows(before, this.comparableRows());
  }

  /** Die aktuellen Zeilen-Objekte, um sie nach einem Neu-Anwenden des Dokuments per {@link rebind} umzuhängen. */
  captureRows(): T[] {
    return [...this.comparableRows()];
  }

  /**
   * Hängt die offenen Zeilen an die neuen Zeilen-Objekte um, nachdem der Editor die Liste neu aufgebaut
   * hat (✗ an einem Einzelfeld wendet das ganze Dokument erneut an, und dabei entstehen neue Objekte).
   * Ohne das zeigten {@link added}/{@link changed} auf verwaiste Objekte: Markierungen weg, Liste bliebe
   * „offen“, und {@link revertPending} ließe vorgeschlagene Zeilen stehen.
   *
   * Zugeordnet wird zuerst über die alte Position, dann über den ersten noch freien inhaltsgleichen
   * Eintrag. Zeilen ohne Gegenstück bleiben wie sie sind.
   */
  rebind(previousRows: T[]): void {
    const diff = this.diff;
    if (!diff) return;
    const currentRows = this.comparableRows();
    const present = new Set(currentRows);
    const tracked = [...diff.added, ...diff.changed.keys()];
    const claimed = new Set(tracked.filter(row => present.has(row)));
    const replacement = new Map<T, T>();
    for (const row of tracked) {
      if (present.has(row)) continue;
      const isFree = (candidate: T | undefined): candidate is T =>
        !!candidate && !claimed.has(candidate) && deepEqual(candidate, row);
      const atOldPosition = currentRows[previousRows.indexOf(row)];
      const match = isFree(atOldPosition) ? atOldPosition : currentRows.find(isFree);
      if (!match) continue;
      claimed.add(match);
      replacement.set(row, match);
    }
    if (!replacement.size) return;
    diff.added = diff.added.map(row => replacement.get(row) ?? row);
    diff.changed = new Map([...diff.changed].map(([row, before]) => [replacement.get(row) ?? row, before]));
  }

  private comparableRows(): T[] {
    return (this.host.comparableRows ?? this.host.rows)();
  }

  /** Vorher/Nachher einer Zeile für `app-change-marker`, `undefined` wenn unverändert. */
  change(row: T, index: number): MyCheckFieldChange | undefined {
    const diff = this.diff;
    if (!diff) return undefined;
    const before = diff.changed.get(row);
    if (!before && !diff.added.includes(row)) return undefined;
    return this.memo(`row:${index}`, [contentKey(before), contentKey(row)],
      (): MyCheckFieldChange => ({before, after: row}));
  }

  /** Wartet diese Zeile (neu oder geändert) noch auf eine Entscheidung? */
  isPending(row: T): boolean {
    const diff = this.diff;
    return !!diff && (diff.changed.has(row) || diff.added.includes(row));
  }

  /**
   * Vorher/Nachher eines einzelnen Felds der Zeile für `app-change-marker`, `undefined` wenn das Feld
   * unverändert ist. Eine neue Zeile markiert alle Felder, eine geänderte nur die tatsächlich
   * abweichenden.
   */
  fieldChange(row: T, index: number, field: string): MyCheckFieldChange | undefined {
    const diff = this.diff;
    if (!diff) return undefined;
    const valueOf = (item: T | undefined): unknown => (item as Record<string, unknown> | undefined)?.[field];
    const before = diff.changed.get(row);
    if (before ? sameFieldValue(field, valueOf(before), valueOf(row)) : !diff.added.includes(row)) return undefined;
    return this.memo(`field:${index}:${field}`, [contentKey(valueOf(before)), contentKey(valueOf(row))],
      (): MyCheckFieldChange => ({before: valueOf(before), after: valueOf(row)}));
  }

  /** ✓ an einer Zeile: so übernehmen, Markierung weg. */
  accept(row: T): void {
    const diff = this.diff;
    if (!diff) return;
    this.accepted = true;
    diff.changed.delete(row);
    diff.added = diff.added.filter(item => item !== row);
    this.host.decided();
  }

  /** ✗ an einer Zeile: neue Zeile entfernen, geänderte auf den Vorher-Stand setzen. */
  reject(row: T): void {
    const diff = this.diff;
    if (!diff) return;
    const before = diff.changed.get(row);
    if (before) {
      for (const key of Object.keys(row)) delete (row as Record<string, unknown>)[key];
      Object.assign(row, JSON.parse(JSON.stringify(before)));
      diff.changed.delete(row);
      this.host.setRows([...this.host.rows()]);
    } else {
      diff.added = diff.added.filter(item => item !== row);
      this.host.setRows(this.host.rows().filter(item => item !== row));
    }
    this.host.decided();
  }

  /** ✓ an einer entfernten Zeile: Entfernen bestätigen. */
  acceptRemoval(entry: { before: T; index: number }): void {
    const diff = this.diff;
    if (!diff) return;
    this.accepted = true;
    diff.removed = diff.removed.filter(item => item !== entry);
    this.host.decided();
  }

  /** ✗ an einer entfernten Zeile: Zeile an ihrer alten Position wiederherstellen. */
  rejectRemoval(entry: { before: T; index: number }): void {
    const diff = this.diff;
    if (!diff) return;
    const rows = [...this.host.rows()];
    rows.splice(Math.min(entry.index, rows.length), 0, JSON.parse(JSON.stringify(entry.before)));
    this.host.setRows(rows);
    diff.removed = diff.removed.filter(item => item !== entry);
    this.host.decided();
  }

  /** Die Liste mit allen noch offenen Zeilenänderungen zurückgesetzt; einzeln übernommene Zeilen bleiben. */
  revertPending(current: T[]): T[] {
    const diff = this.diff;
    if (!diff) return current;
    const rows = current.filter(row => !diff.added.includes(row)).map(row => diff.changed.get(row) ?? row);
    [...diff.removed].sort((a, b) => a.index - b.index)
      .forEach(({before, index}) => rows.splice(Math.min(index, rows.length), 0, before));
    return rows;
  }
}
